import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from "@/components/ui/accordion";
import {
  LayoutDashboard, Building2, UserCog, Users, FileText, ArrowLeftRight,
  BarChart3, LifeBuoy, Shield, Activity, Settings, ScrollText, BookOpen,
  User, ChevronRight
} from "lucide-react";

const SUPER_ADMIN_MODULES = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    summary: "Get a bird's-eye view of your entire fund administration platform — total funds, investors, AUM, recent activity, and key performance charts.",
    steps: [
      "After logging in, the Dashboard is your landing page with real-time statistics.",
      "Review the stat cards at the top for total funds, active investors, total AUM, and recent transactions.",
      "Use the charts section to visualize AUM trends and transaction volumes over time.",
      "The Recent Activity section shows the latest actions across all users — logins, investor additions, document uploads, etc.",
      "Use the fund filter dropdown to narrow stats to a specific fund."
    ]
  },
  {
    id: "fund-management",
    title: "Fund Management",
    icon: Building2,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    summary: "Create and manage funds. Upload a PPM document and let AI auto-fill fund details, or create funds manually. Configure offerings, fees, workflows, and onboarding requirements.",
    steps: [
      "Navigate to Fund Management from the sidebar.",
      "Click 'Create Fund' to start a new fund. You can upload a PPM (PDF) for AI-powered auto-extraction of fund details, or create one manually.",
      "After creation, click on any fund card to open the Fund Detail page.",
      "In Fund Detail, you can edit fund information — name, type, strategy, currency, inception date, reporting start date, and reporting frequency.",
      "Under the 'Offerings' tab, manage offerings (e.g., Class A, Class B) with their specific fees — management fee, performance fee, hurdle rate, high watermark, etc.",
      "The 'Offering Terms' tab lets you toggle which fee terms are applicable for each offering.",
      "The 'Workflow' tab configures the investor onboarding process — define required fields and required documents that investors must complete.",
      "Use the 'Fund Managers' section to assign or remove fund managers from a fund.",
      "The fund status can be toggled between Active and Inactive."
    ]
  },
  {
    id: "user-management",
    title: "User Management",
    icon: UserCog,
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    summary: "Manage all platform users — create Fund Managers, assign them to funds, activate/deactivate accounts, and reset credentials.",
    steps: [
      "Go to User Management from the sidebar.",
      "Click 'Add User' to create a new Fund Manager. Enter their name and email — a temporary password will be auto-generated.",
      "The new user will receive an email with their credentials (if outgoing emails are enabled in System Config).",
      "To assign a fund manager to a fund, go to the specific fund's detail page under Fund Management.",
      "Use the status toggle to activate or deactivate a user account.",
      "Click on any user to view their details, assigned funds, and last login information."
    ]
  },
  {
    id: "investors",
    title: "Investors",
    icon: Users,
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    summary: "Onboard investors into funds, manage their profiles, KYC status, documents, and subscribed offerings. Track onboarding completion.",
    steps: [
      "Navigate to Investors from the sidebar. You'll see a list of all investors across all funds.",
      "Click 'Add Investor' to onboard a new investor. Select the fund, fill in their details (name, email, investor type), and choose which offerings they are subscribing to.",
      "You can optionally upload a subscription document (PDF) for AI-assisted extraction of investor details.",
      "After creation, the investor receives a welcome email with login credentials (if notifications are enabled).",
      "Click on any investor row to open their detail page.",
      "In the detail page, review and update personal info, KYC status (Pending, Under Review, Verified, Expired, Flagged), and subscribed offerings.",
      "The 'Documents' tab shows all documents uploaded by or for the investor. You can upload new documents here.",
      "The 'Onboarding' tab tracks the investor's progress through the workflow requirements — required fields and documents defined in the fund's workflow."
    ]
  },
  {
    id: "documents",
    title: "Documents",
    icon: FileText,
    color: "text-pink-500",
    bg: "bg-pink-500/10",
    summary: "Central document repository — upload, categorize, and manage documents across all funds and investors.",
    steps: [
      "Go to Documents from the sidebar.",
      "View all documents with filters for fund, category (Onboarding, Declarations, Notices, Agreements, Financial Reports, Other), and status.",
      "Click 'Upload Document' to add new documents. Select the fund and investor, choose a category, and upload the file.",
      "Each document shows its upload date, associated fund, investor, category, and file size.",
      "Click on a document to download it."
    ]
  },
  {
    id: "transactions",
    title: "Transactions",
    icon: ArrowLeftRight,
    color: "text-cyan-500",
    bg: "bg-cyan-500/10",
    summary: "Record and manage capital transactions (subscriptions, redemptions) and performance transactions (NAV, returns) for all investors.",
    steps: [
      "Navigate to Transactions from the sidebar.",
      "Use the tabs to switch between 'Capital Transactions' and 'Performance Transactions'.",
      "For Capital Transactions: Click 'Add Transaction' to record a subscription (investment) or redemption. Select the fund, investor, offering, enter the amount and date.",
      "You can also import multiple transactions via CSV upload.",
      "For Performance Transactions: Record NAV (Net Asset Value), returns, and other performance data per investor per offering per period.",
      "Performance transactions have a 'submitted' or 'released' status — once released, they appear in investor statements.",
      "Use filters to narrow transactions by fund, investor, date range, or type."
    ]
  },
  {
    id: "reporting",
    title: "Reporting",
    icon: BarChart3,
    color: "text-indigo-500",
    bg: "bg-indigo-500/10",
    summary: "Generate and manage investor statements and roll-on reports. Release reports to investors and generate PDF statements.",
    steps: [
      "Go to Reporting from the sidebar.",
      "Select a fund and reporting period from the dropdowns.",
      "The 'Roll-On Report' tab shows a comprehensive table of all investor positions — subscriptions, NAV, returns per offering per period.",
      "Use 'Release All' to release all investor reports for a period at once, or release individual investors.",
      "The 'Statements' tab lets you generate PDF statements for individual investors. Select the template, investor, and period.",
      "Released reports become visible to investors in their portal.",
      "Reporting periods are auto-generated based on the fund's reporting frequency (monthly, quarterly, annually) and reporting start date."
    ]
  },
  {
    id: "support",
    title: "Support",
    icon: LifeBuoy,
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    summary: "Manage support tickets raised by fund managers and investors. Track ticket status from open to resolved.",
    steps: [
      "Navigate to Support from the sidebar.",
      "View all support tickets with filters for status (Open, In Progress, Resolved, Closed) and category.",
      "Click on a ticket to view its full details, including the conversation thread.",
      "Reply to tickets directly from the detail view. Your responses are visible to the ticket creator.",
      "Update the ticket status as you work through it — from Open to In Progress to Resolved to Closed.",
      "Email notifications are sent when new tickets are created (if configured in System Config)."
    ]
  },
  {
    id: "governance",
    title: "Fund Oversight",
    icon: Shield,
    color: "text-teal-500",
    bg: "bg-teal-500/10",
    summary: "Monitor platform-wide governance — investor KYC compliance, escalation tracking, and oversight controls.",
    steps: [
      "Go to Fund Oversight from the sidebar.",
      "Review the KYC overview to see investor compliance status across all funds.",
      "The Escalation section tracks flagged items that need attention — expired KYC, pending documents, etc.",
      "Create new escalations to flag issues for follow-up.",
      "Use this section to ensure regulatory compliance across your fund administration."
    ]
  },
  {
    id: "status",
    title: "Status",
    icon: Activity,
    color: "text-green-500",
    bg: "bg-green-500/10",
    summary: "Monitor platform health — system status, service availability, and recent deployment information.",
    steps: [
      "Navigate to Status from the sidebar.",
      "View the current status of all platform services.",
      "Check for any ongoing incidents or maintenance windows.",
      "This page helps you verify that all systems are operational."
    ]
  },
  {
    id: "system-config",
    title: "System Configuration",
    icon: Settings,
    color: "text-slate-500",
    bg: "bg-slate-500/10",
    summary: "Configure platform-wide settings — branding (logo, company name), email notification preferences, and custom disclaimers.",
    steps: [
      "Go to System Config from the sidebar.",
      "Under 'Branding', upload your company logo, set your organization name, website URL, and custom disclaimer text for statements.",
      "Under 'Notification Preferences', toggle which email notifications are active:",
      "  - 'Outgoing Emails' is the master switch — when OFF, no emails are sent.",
      "  - Individual toggles: New Investor, Document Upload, Transaction, KYC Change, Onboarding Complete, Report Released.",
      "Changes save automatically and take effect immediately across the platform.",
      "The branding settings are used in investor statement PDFs and email templates."
    ]
  },
  {
    id: "statement-templates",
    title: "Statement Templates",
    icon: FileText,
    color: "text-rose-500",
    bg: "bg-rose-500/10",
    summary: "Preview and manage the PDF templates used for investor statements — NAV Statement, Equity Statement, and Partner Capital Account.",
    steps: [
      "Navigate to Statement Templates from the sidebar.",
      "Browse the available PDF templates: NAV Statement, Equity Statement, and Partner Capital Account.",
      "Click on a template to preview it with sample data.",
      "Templates automatically use your branding (logo, name, disclaimer) from System Config.",
      "These templates are used when generating investor statements from the Reporting section."
    ]
  },
  {
    id: "audit-logs",
    title: "Audit Logs",
    icon: ScrollText,
    color: "text-gray-500",
    bg: "bg-gray-500/10",
    summary: "Track every action performed on the platform — who did what, when, and from where. Essential for compliance and accountability.",
    steps: [
      "Go to Audit Logs from the sidebar.",
      "View a chronological list of all platform actions — logins, user creation, fund updates, investor changes, document uploads, etc.",
      "Each log entry shows: timestamp, user name, email, action type, and details.",
      "Use the search and filter options to find specific actions or narrow by user, action type, or date range.",
      "Audit logs cannot be modified or deleted — they serve as a permanent record of platform activity."
    ]
  }
];

const ADMIN_MODULES = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    summary: "Your fund management overview — key stats for your assigned funds including investor count, total AUM, and recent activity.",
    steps: [
      "After logging in, the Dashboard shows a summary of the funds you manage.",
      "Review stat cards for total investors, AUM, and recent transaction volumes for your funds.",
      "The Recent Activity section highlights the latest actions related to your funds.",
      "If you manage multiple funds, use the fund selector to switch between them."
    ]
  },
  {
    id: "fund-profile",
    title: "Fund Profile",
    icon: Building2,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    summary: "View and edit your fund's details — strategy, terms, offerings, fee structures, and investor onboarding workflow. If you manage multiple funds, switch between them easily.",
    steps: [
      "Navigate to Fund Profile from the sidebar.",
      "If you manage multiple funds, a fund selector dropdown appears at the top — choose which fund to view.",
      "Review and edit fund information: name, type, strategy, currency, and reporting settings.",
      "Manage Offerings (e.g., Class A, Class B): configure management fees, performance fees, hurdle rates, and other fee terms per offering.",
      "The 'Offering Terms' tab shows which fee structures are applicable for each offering.",
      "Under 'Workflow', review the investor onboarding requirements — required fields and documents.",
      "Changes you make here are reflected across the investor experience and reporting."
    ]
  },
  {
    id: "investors",
    title: "Investors",
    icon: Users,
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    summary: "Manage investors in your funds — onboard new investors, track their KYC status, review documents, and manage subscribed offerings.",
    steps: [
      "Go to Investors from the sidebar to see all investors in your assigned funds.",
      "Click 'Add Investor' to onboard a new investor into one of your funds.",
      "Fill in the investor's details — name, email, investor type — and select their subscribed offerings.",
      "Optionally upload a subscription document for AI-assisted data extraction.",
      "Click on any investor to open their detail page where you can:",
      "  - Update personal information and KYC status.",
      "  - Manage their subscribed offerings.",
      "  - Upload and review documents.",
      "  - Track their onboarding progress through required steps."
    ]
  },
  {
    id: "documents",
    title: "Documents",
    icon: FileText,
    color: "text-pink-500",
    bg: "bg-pink-500/10",
    summary: "Access and manage all documents related to your funds — investor documents, agreements, reports, and notices.",
    steps: [
      "Navigate to Documents from the sidebar.",
      "View all documents for your assigned funds with filters by category and status.",
      "Upload new documents — select the fund, investor (if applicable), choose a category, and upload the file.",
      "Click on any document to download it.",
      "Documents uploaded here are visible to the associated investors in their portal."
    ]
  },
  {
    id: "transactions",
    title: "Transactions",
    icon: ArrowLeftRight,
    color: "text-cyan-500",
    bg: "bg-cyan-500/10",
    summary: "Record and manage capital transactions (subscriptions, redemptions) and performance data (NAV, returns) for investors in your funds.",
    steps: [
      "Go to Transactions from the sidebar.",
      "Switch between 'Capital Transactions' and 'Performance Transactions' using the tabs.",
      "Capital Transactions: Record subscriptions (money in) and redemptions (money out) for investors. Specify the offering, amount, and date.",
      "You can import multiple transactions at once via CSV upload.",
      "Performance Transactions: Enter NAV and return data per investor per offering per period.",
      "Mark performance transactions as 'submitted' first, then 'released' when ready for investors to see.",
      "Use filters to find specific transactions by investor, date, or type."
    ]
  },
  {
    id: "reporting",
    title: "Reporting",
    icon: BarChart3,
    color: "text-indigo-500",
    bg: "bg-indigo-500/10",
    summary: "Generate investor statements and roll-on reports for your funds. Release reports to make them available to investors.",
    steps: [
      "Navigate to Reporting from the sidebar.",
      "If you manage multiple funds, select the fund from the dropdown at the top.",
      "Choose a reporting period — periods are auto-generated based on the fund's reporting frequency.",
      "View the Roll-On Report to see all investor positions, NAV, and returns across offerings.",
      "Release individual or all investor reports for a period using the release buttons.",
      "Generate PDF statements for investors by selecting a template and investor from the Statements tab.",
      "Released reports become visible to investors in their Reporting section."
    ]
  },
  {
    id: "support",
    title: "Support",
    icon: LifeBuoy,
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    summary: "Create and manage support tickets to communicate with the platform administrators. Track your requests and receive responses.",
    steps: [
      "Go to Support from the sidebar.",
      "View all support tickets related to your funds.",
      "Click 'New Ticket' to create a support request. Choose a category (General Inquiry, Document Request, Transaction Query, Technical Issue), add a subject and message.",
      "Track the status of your tickets: Open, In Progress, Resolved, Closed.",
      "Click on a ticket to view the full conversation and add replies.",
      "You'll receive notifications when administrators respond to your tickets."
    ]
  }
];

const INVESTOR_MODULES = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    summary: "Your personal investment overview — see your total invested amount, current NAV, returns, and recent account activity at a glance.",
    steps: [
      "After logging in, the Dashboard is your home page showing your investment summary.",
      "The stat cards show your key figures: total invested capital, current NAV (Net Asset Value), and returns.",
      "Review the fund card(s) to see which fund(s) you are invested in, along with your subscribed offerings.",
      "The Recent Activity section shows your latest transactions and document updates.",
      "Click on the fund name to go to the Fund Profile for more details about the fund."
    ]
  },
  {
    id: "profile",
    title: "Profile",
    icon: User,
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    summary: "View and manage your personal information — contact details, investor type, KYC status, and subscribed offerings.",
    steps: [
      "Navigate to Profile from the sidebar.",
      "Review your personal details: name, email, investor type, and contact information.",
      "Check your KYC (Know Your Customer) status — this indicates whether your identity verification is complete.",
      "View your subscribed offerings to see which investment classes you participate in.",
      "If any information needs updating, contact your fund manager or submit a support ticket.",
      "Your onboarding status shows whether all required steps (documents, forms) have been completed."
    ]
  },
  {
    id: "documents",
    title: "Documents",
    icon: FileText,
    color: "text-pink-500",
    bg: "bg-pink-500/10",
    summary: "Access all your investment documents — subscription agreements, tax forms, fund notices, and financial reports. Download anytime.",
    steps: [
      "Go to Documents from the sidebar.",
      "Browse all documents associated with your account, organized by category: Onboarding, Declarations, Notices, Agreements, Financial Reports.",
      "Click on any document to download it to your device.",
      "New documents uploaded by your fund manager will appear here automatically.",
      "Use the category filter to quickly find specific document types."
    ]
  },
  {
    id: "transactions",
    title: "Transactions",
    icon: ArrowLeftRight,
    color: "text-cyan-500",
    bg: "bg-cyan-500/10",
    summary: "View your complete transaction history — all subscriptions (investments) and redemptions (withdrawals), with amounts, dates, and offering details.",
    steps: [
      "Navigate to Transactions from the sidebar.",
      "View a chronological list of all your capital transactions.",
      "Each transaction shows: date, type (Subscription or Redemption), offering, amount, and status.",
      "Use the date filter to view transactions within a specific period.",
      "This section is read-only — transactions are recorded by your fund manager. If you notice any discrepancies, raise a support ticket."
    ]
  },
  {
    id: "reporting",
    title: "Reporting",
    icon: BarChart3,
    color: "text-indigo-500",
    bg: "bg-indigo-500/10",
    summary: "Access your investor statements and performance reports. Download PDF statements for your records and tax purposes.",
    steps: [
      "Go to Reporting from the sidebar.",
      "View released reports organized by reporting period (monthly, quarterly, or annually depending on your fund).",
      "Each report shows your position summary: invested amount, NAV, returns, and per-offering breakdowns.",
      "Click 'Download Statement' to get a PDF version of your investor statement.",
      "Only released reports are visible here — your fund manager controls when reports are made available.",
      "Keep your statements for your records and tax filing."
    ]
  },
  {
    id: "support",
    title: "Support",
    icon: LifeBuoy,
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    summary: "Need help? Create support tickets to reach your fund administrator. Ask questions about your account, documents, transactions, or any other concerns.",
    steps: [
      "Navigate to Support from the sidebar.",
      "Click 'New Ticket' to create a new support request.",
      "Select a category that best matches your inquiry: General Inquiry, Document Request, Transaction Query, or Technical Issue.",
      "Add a clear subject line and describe your question or concern in detail.",
      "After submitting, you can track the status of your ticket: Open, In Progress, Resolved, or Closed.",
      "Click on a ticket to view responses from the administrator and add follow-up messages.",
      "You'll be notified when your ticket receives a response."
    ]
  }
];

const ROLE_CONTENT = {
  super_admin: {
    heading: "Super Admin Knowledge Bank",
    description: "Complete guide to managing the Tychi Fund Administration platform. Learn how to use each module to set up funds, onboard investors, manage transactions, generate reports, and configure the system.",
    modules: SUPER_ADMIN_MODULES
  },
  admin: {
    heading: "Fund Manager Knowledge Bank",
    description: "Your guide to managing funds and investors on the Tychi platform. Learn how to use each module to manage your assigned funds, onboard investors, record transactions, and generate reports.",
    modules: ADMIN_MODULES
  },
  investor: {
    heading: "Investor Knowledge Bank",
    description: "Welcome to your investor guide. Learn how to navigate your portal, view your investments, access documents, track transactions, download statements, and get support.",
    modules: INVESTOR_MODULES
  }
};

export default function KnowledgeBankPage() {
  const { user } = useAuth();
  const content = ROLE_CONTENT[user?.role] || ROLE_CONTENT.investor;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="knowledge-bank-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10">
          <BookOpen className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" data-testid="knowledge-bank-heading">
            {content.heading}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{content.description}</p>
        </div>
      </div>

      <Card className="border-border bg-card/50">
        <CardContent className="p-0">
          <Accordion type="single" collapsible className="w-full" data-testid="knowledge-bank-accordion">
            {content.modules.map((mod) => (
              <AccordionItem key={mod.id} value={mod.id} className="border-border px-6">
                <AccordionTrigger className="hover:no-underline py-5 gap-3" data-testid={`kb-module-${mod.id}`}>
                  <div className="flex items-center gap-3 text-left">
                    <div className={`p-2 rounded-lg ${mod.bg} flex-shrink-0`}>
                      <mod.icon className={`w-4 h-4 ${mod.color}`} />
                    </div>
                    <div>
                      <span className="font-semibold text-foreground text-sm">{mod.title}</span>
                      <p className="text-xs text-muted-foreground mt-0.5 font-normal leading-relaxed">{mod.summary}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-12 pb-2 space-y-2" data-testid={`kb-steps-${mod.id}`}>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider mb-3">
                      Step-by-step Guide
                    </Badge>
                    <ol className="space-y-2.5">
                      {mod.steps.map((step, i) => (
                        <li key={i} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                          {step.startsWith("  -") ? (
                            <>
                              <span className="w-5 flex-shrink-0" />
                              <ChevronRight className="w-3 h-3 mt-1.5 flex-shrink-0 text-muted-foreground/60" />
                              <span>{step.trim().replace(/^- /, "")}</span>
                            </>
                          ) : (
                            <>
                              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {step.startsWith("  ") ? "" : i + 1 - mod.steps.slice(0, i).filter(s => s.startsWith("  ")).length}
                              </span>
                              <span>{step}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
